import React, { ElementType, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Flex,
  HStack,
  Icon,
  IconButton,
  Spinner,
  Text,
  useToast,
} from '@chakra-ui/react';
import { MessageSquareMore, Settings2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { FaFacebook } from 'react-icons/fa';
import {
  getFacebookConversations,
  getFacebookProfile,
} from '../../../hooks/facebook';
import { FacebookConversation, FacebookProfile } from '../../../utils/types';
import {
  FacebookConversationRow,
  FacebookSearchBar,
  FacebookSidebarEmpty,
  fbBorder,
} from '../components/facebookMessengerUi';

interface ChatSidebarProps {
  orgId: string;
  facebookAccId: string;
  selectedConversationId?: string;
}

const FacebookChatSidebar: React.FC<ChatSidebarProps> = ({
  orgId,
  facebookAccId,
  selectedConversationId,
}) => {
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [chats, setChats] = useState<FacebookConversation[]>([]);
  const [accountData, setAccountData] = useState<FacebookProfile>();

  const toast = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [profileResponse, conversationsResponse] = await Promise.all([
          getFacebookProfile(orgId),
          getFacebookConversations(orgId),
        ]);

        if (profileResponse.success && Array.isArray(profileResponse.data)) {
          setAccountData(profileResponse.data[0]);
        }

        if (conversationsResponse.success) {
          const normalized = Array.isArray(conversationsResponse.data)
            ? conversationsResponse.data
            : conversationsResponse.data?.results || [];
          setChats(normalized);
        } else {
          throw new Error('Failed to fetch conversations.');
        }
      } catch (error) {
        toast({
          title: 'Unable to load conversations',
          description: 'Try refreshing the inbox in a moment.',
          status: 'error',
          duration: 4000,
          isClosable: true,
        });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [orgId, toast]);

  const filteredChats = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return chats;
    return chats.filter((conversation) =>
      [
        conversation.sender_name,
        conversation.sender_id,
        conversation.last_message?.text,
      ]
        .filter(Boolean)
        .some((value) => value!.toLowerCase().includes(query)),
    );
  }, [chats, search]);

  return (
    <Flex direction="column" h="calc(100vh - 81px)" bg="white">
      <Box px={4} py={3} borderBottom="1px solid" borderColor={fbBorder}>
        <HStack justify="space-between">
          <HStack spacing={3}>
            <Icon as={FaFacebook as ElementType} boxSize={7} color="#1877F2" />
            <Text fontWeight="700" color="gray.700">
              {accountData?.page_name || 'Facebook Inbox'}
            </Text>
          </HStack>
          <HStack spacing={1}>
            <IconButton
              aria-label="Comments"
              icon={<Icon as={MessageSquareMore as ElementType} boxSize={4} />}
              variant="ghost"
              borderRadius="12px"
              onClick={() =>
                navigate(`/organization/${orgId}/inbox/facebook/${facebookAccId}/comments`)
              }
            />
            <IconButton
              aria-label="Page settings"
              icon={<Icon as={Settings2 as ElementType} boxSize={4} />}
              variant="ghost"
              borderRadius="12px"
              onClick={() =>
                navigate(`/organization/${orgId}/inbox/facebook/${facebookAccId}/settings`)
              }
            />
          </HStack>
        </HStack>
      </Box>

      <FacebookSearchBar
        value={search}
        onChange={setSearch}
        placeholder="Search by customer or message"
      />

      <Box flex="1" overflowY="auto">
        {loading ? (
          <Flex justify="center" align="center" h="160px">
            <Spinner color="#1877F2" />
          </Flex>
        ) : filteredChats.length === 0 ? (
          <FacebookSidebarEmpty
            title={search ? 'No matching conversations' : 'No conversations yet'}
            description={
              search
                ? 'Try a different name or keyword.'
                : 'New customer messages from your connected Facebook Page will appear here.'
            }
          />
        ) : (
          filteredChats.map((conversation) => (
            <FacebookConversationRow
              key={conversation.id}
              conversation={conversation}
              isActive={selectedConversationId === conversation.id}
              href={`/organization/${orgId}/inbox/facebook/conversations/${conversation.id}`}
            />
          ))
        )}
      </Box>
    </Flex>
  );
};

export default FacebookChatSidebar;
